import { Controller } from "@hotwired/stimulus"

/**
 * Equipment Set Item Controller
 * Manages equipment selection for Equipment Sets forms
 * Each equipment type section (Instrument, Probe, Calibration Block, Reference Block) has its own instance
 */
export default class extends Controller {
  static targets = ["container", "select", "hiddenFields", "badge"]
  static values = {
    type: Number  // Equipment type ID (1=Instrument, 2=Probe, 3=Cal Block, 4=Ref Block)
  }

  connect() {
    console.log('[equipment-set-item] Controller connected, type:', this.typeValue)
    this.equipmentData = new Map()
    this.initializeExistingEquipment()
    console.log('[equipment-set-item] Initial equipment count:', this.equipmentData.size)
  }

  /**
   * Load existing equipment from the rendered table rows
   */
  initializeExistingEquipment() {
    const rows = this.containerTarget.querySelectorAll('tr[data-equipment-id]')
    
    rows.forEach(row => {
      const id = row.dataset.equipmentId
      const cells = row.querySelectorAll('td')
      
      this.equipmentData.set(id, {
        name: cells[1]?.textContent?.trim() || '',  // NAME is column 1
        maker: cells[2]?.textContent?.trim() || '',  // MAKER is column 2
        serialNo: cells[3]?.textContent?.trim() || '' // SERIAL NO. is column 3
      })
    })
  }

  /**
   * Add equipment from dropdown selection
   */
  addEquipment(event) {
    event.preventDefault()

    const select = this.selectTarget
    const equipmentId = select.value

    console.log('[equipment-set-item] Adding equipment, selected value:', equipmentId)

    if (!equipmentId) {
      alert('Please select equipment to add')
      return
    }

    if (this.equipmentData.has(equipmentId)) {
      alert('This equipment is already added')
      this.resetSelect()
      return
    }

    console.log('[equipment-set-item] Fetching equipment data for ID:', equipmentId)

    // Fetch equipment data from server
    fetch(`/equipment/${equipmentId}.json`)
      .then(response => {
        if (!response.ok) throw new Error('Failed to fetch equipment data')
        return response.json()
      })
      .then(data => {
        this.equipmentData.set(equipmentId, {
          name: data.report_text_or_name || data.name || '',
          maker: data.maker || '',
          serialNo: data.serial_no || ''
        })
        this.renderEquipment()
        this.updateHiddenFields()
        this.resetSelect()
      })
      .catch(error => {
        console.error('[equipment-set-item] Error fetching equipment:', error)
        alert('Failed to add equipment')
      })
  }

  /**
   * Remove equipment from the list
   */
  removeEquipment(event) {
    event.preventDefault()
    const equipmentId = event.currentTarget.dataset.equipmentId
    
    if (!equipmentId) return
    
    this.equipmentData.delete(equipmentId)
    this.renderEquipment()
    this.updateHiddenFields()
  }

  /**
   * Render the equipment table or empty state
   */
  renderEquipment() {
    if (this.equipmentData.size === 0) {
      this.containerTarget.innerHTML = `
        <div class="text-muted text-center py-3">
          <i class="fa ${this.getIconForType()} mr-2"></i>
          No ${this.getLabelForType()} selected
        </div>
      `
    } else {
      let html = `
        <div class="table-responsive">
          <table class="table table-sm table-hover">
            <thead>
              <tr>
                <th>Name</th>
                <th>Maker</th>
                <th>Serial No.</th>
                <th style="width: 50px;">Action</th>
              </tr>
            </thead>
            <tbody>
      `
      
      this.equipmentData.forEach((eq, id) => {
        html += `
          <tr data-equipment-id="${id}">
            <td>${this.escapeHtml(eq.name)}</td>
            <td>${this.escapeHtml(eq.maker)}</td>
            <td>${this.escapeHtml(eq.serialNo)}</td>
            <td>
              <button type="button" class="btn btn-sm btn-link text-danger p-0" 
                      data-action="click->equipment-set-item#removeEquipment" 
                      data-equipment-id="${id}"
                      title="Remove">
                <i class="fa fa-trash"></i>
              </button>
            </td>
          </tr>
        `
      })
      
      html += '</tbody></table></div>'
      this.containerTarget.innerHTML = html
    }
    
    // Update badge if present
    if (this.hasBadgeTarget) {
      this.badgeTarget.textContent = this.equipmentData.size
    }
  }

  /**
   * Update hidden fields for form submission
   */
  updateHiddenFields() {
    let html = ''
    const ids = []
    this.equipmentData.forEach((_, id) => {
      ids.push(id)
      html += `<input type="hidden" name="ut_equipment_set[equipment_ids][]" value="${id}" />\n`
    })
    console.log('[equipment-set-item] Updating hidden fields with IDs:', ids)
    this.hiddenFieldsTarget.innerHTML = html
  }

  /**
   * Reset the select dropdown
   */
  resetSelect() {
    this.selectTarget.value = ''
    // Reset Select2 if present
    if (window.jQuery && window.jQuery(this.selectTarget).data('select2')) {
      window.jQuery(this.selectTarget).val('').trigger('change')
    }
  }

  /**
   * Get icon class for equipment type
   */
  getIconForType() {
    const icons = {
      1: 'fa-microchip',
      2: 'fa-broadcast-tower',
      3: 'fa-cube',
      4: 'fa-shapes'
    }
    return icons[this.typeValue] || 'fa-wrench'
  }

  /**
   * Get label for equipment type
   */
  getLabelForType() {
    const labels = {
      1: 'instrument',
      2: 'probes',
      3: 'calibration blocks',
      4: 'reference blocks'
    }
    return labels[this.typeValue] || 'equipment'
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text || ''
    return div.innerHTML
  }
}
